import { Component, OnInit, ViewChild } from '@angular/core';
import { CodeInputComponent } from 'angular-code-input';
import { NotificationService } from '../../services/notification.service';
import { SegmentCMOSService } from '../../services/segment-cmos.service';
import { UserService } from '../../services/user.service';
import { Logger } from '../../util/logger/logger.service';
import { LoadingController, NavController, Platform, ToastController } from '@ionic/angular';
import { presentLoader } from '../../util/ion-util';
import { removeNonDigits } from 'src/app/util/mask-util';
import { AppRoutingUtil } from 'src/app/util/app-routing-util';
import { getApplicationHomeQueryParamString } from 'src/app/util/job-util';
import { ActivatedRoute } from '@angular/router';
import { Auth } from 'aws-amplify';
import { fromE164 } from 'src/app/util/phone-util';
import { environment } from 'src/environments/environment';
import { CognitoUser } from 'amazon-cognito-identity-js';
import { EXPIRED_CODE, INVALID_VERIFICATION_CODE } from 'src/app/util/cognito-error-util';
import { ResendCodeComponent } from "../resend-code/resend-code.component";

@Component({
  selector: 'app-verification-code',
  templateUrl: './verification-code.component.html',
  styleUrls: ['./verification-code.component.scss'],
})
export class VerificationCodeComponent implements OnInit {

  @ViewChild('codeInput')
  private codeInput: CodeInputComponent;

  public error: any;

  public phone: string;

  cognitoUser: CognitoUser | any;

  newPhoneNumber: string;

  editProfileFlow: boolean = false;
  codeSent: boolean = false;
  infoMessage: string | null = null;

  constructor(private activatedRoute: ActivatedRoute,
    private navCtrl: NavController,
    private userService: UserService,
    private logger: Logger,
    private platform: Platform,
    private loadingCtrl: LoadingController,
    private toastCtrl: ToastController,
    private segmentCMOS: SegmentCMOSService,
    private notificationService: NotificationService) {
  }

  async ngOnInit() {
    this.cognitoUser = await this.userService.getCognitoUser(true);
    this.activatedRoute.queryParams.subscribe((result) => {
      this.editProfileFlow = result['editProfileFlow'];
      this.codeSent = result['codeSent'];
      this.newPhoneNumber = result['newPhoneNumber'];
      const displayPhoneNumber = this.newPhoneNumber ? this.newPhoneNumber : fromE164(this.cognitoUser.attributes.phone_number);
      const cleaned = removeNonDigits('' + displayPhoneNumber);
      const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
      if (match) {
        const intlCode = (match[1] ? '' : '');
        this.phone = [intlCode, '(', match[2], ') ', match[3], '-', match[4]].join('');
      }
    });
  }

  // this called only if user entered full code
  async onCodeCompleted(code: string) {
    const loader = await presentLoader(this.loadingCtrl, 30000);
    let phoneNumberToUpdate
    const oldPhoneNumber = this.userService.currentUser.userInfo.personalInformation.phone
    // Allow e2e and local testing to bypass code verification
    if (!environment.production && code === '111111' && !this.codeSent) {
      phoneNumberToUpdate = this.newPhoneNumber;
      this.userService.updateUserProfile({ phone: phoneNumberToUpdate })
        .subscribe({
          next: async () => {
            if (this.platform.is('hybrid')) {
              try {
                await this.notificationService.registerPushNotifications();
              } catch { }
            }
            await loader.dismiss();
            await this.navCtrl.navigateRoot(AppRoutingUtil.BROWSE_JOBS);
          },
          error: async error => {
            await loader.dismiss();
            this.error = error.error.reason;
          }
        });
    } else if (!environment.production && code === '111111' && this.codeSent) {
      await loader.dismiss();
      this.userService.setVerificationCode(code);
      this.navCtrl.navigateForward(AppRoutingUtil.USER_INFO);
    } else {
      try {
        await Auth.verifyUserAttributeSubmit(this.cognitoUser, 'phone_number', code);
        if (this.codeSent && !this.editProfileFlow) {
          await loader.dismiss();
          await this.userService.setVerificationCode(code);
          this.navCtrl.navigateForward(AppRoutingUtil.USER_INFO);
        } else {
          // get updated phone number from cognito after verification
          phoneNumberToUpdate = fromE164((await this.userService.getCognitoUser(true)).attributes.phone_number);

          // do api-v1 stuff
          this.userService.updateUserProfile({ phone: phoneNumberToUpdate })
            .subscribe({
              next: async () => {
                if (this.platform.is('hybrid')) {
                  try {
                    await this.notificationService.registerPushNotifications();
                  } catch {
                  }
                }
                await this.segmentCMOS.addEvent('phone_verification_completed', {
                  phone: phoneNumberToUpdate,
                  verification_code: code,
                  verificationFailed: false
                })
                await loader.dismiss();
                const jobId = await this.userService.getJobId();
                const jobIds = await this.userService.getJobIds();
                if (this.editProfileFlow) {
                  this.segmentCMOS.addEvent('updatePhoneNumberSelfService_success', {
                    oldPhoneNumber: oldPhoneNumber,
                    newPhoneNumber: phoneNumberToUpdate
                  });
                  await this.userService.setVerificationCode(code);
                  await this.onDismiss();
                  await this.presentToast('Phone number updated');
                } else if (jobIds) {
                  await this.userService.setJobIds(null);
                  await this.navCtrl.navigateRoot(`/application-home${getApplicationHomeQueryParamString(jobIds)}`);
                } else if (jobId) {
                  await this.userService.setJobId(null);
                  await this.navCtrl.navigateRoot(`/application-home?externalId=${jobId}`);
                } else {
                  await this.navCtrl.navigateRoot(AppRoutingUtil.BROWSE_JOBS);
                }
              },
              error: async error => {
                await loader.dismiss();
                this.segmentCMOS.addEvent('phone_verification_failed', {
                  phone: phoneNumberToUpdate,
                  verification_code: code,
                  verificationFailed: true
                })
                this.error = error.error.reason;
              }
            });
        }
      } catch (error) {
        if (EXPIRED_CODE.toLowerCase() === error.message.toLowerCase() || INVALID_VERIFICATION_CODE.toLowerCase() === error.message.toLowerCase()) {
          this.logger.log(error);
          this.segmentCMOS.addEvent('phone_verification_failed', {
            phone: phoneNumberToUpdate,
            verification_code: code,
            verificationFailed: true
          })
          if (this.editProfileFlow) {
            this.segmentCMOS.addEvent('updatePhoneNumberSelfService_failure', {
              reason: error?.error?.reason,
              details: error?.error?.details,
              message: error?.message,
            });
          }
          this.error = true;
          this.codeInput.reset();
          this.codeInput.focusOnField(0);
          this.error = 'ER_INVALID_CODE';
        }
        await loader.dismiss();
      }
    }
  }

  onCodeChanged(event: any) { }

  async onDismiss() {
    this.navCtrl.navigateRoot('/', { skipLocationChange: true }).then(() =>
      this.navCtrl.navigateForward(AppRoutingUtil.USER_INFO_PROFILE));
  }

  async toHome() {
    this.navCtrl.navigateRoot('/', { skipLocationChange: true }).then(() =>
      this.navCtrl.navigateForward(AppRoutingUtil.DASHBOARD));
  }

  editPhoneNumberClicked(clearError: boolean) {
    if (this.editProfileFlow) {
      this.navCtrl.navigateForward(AppRoutingUtil.ACCOUNT_RESEND_CODE, { queryParams: { editProfileFlow: true } })
    } else {
      this.navCtrl.navigateForward(AppRoutingUtil.ACCOUNT_RESEND_CODE);
    }
    if (clearError) {
      this.error = null;
    }
  }

  async presentToast(toastMessage: string) {
    // Stop multiple toasts
    try {
      await this.toastCtrl.dismiss();
    } catch (err) { }

    const toast = await this.toastCtrl.create({
      cssClass: 'success-toast',
      message: '<img src="assets/icons/completed-check.svg"></img>' + toastMessage,
      duration: 2000,
      position: 'bottom',
    });
    toast.present();
  }

  async sendCode() {
    this.error = null;
    this.infoMessage = "Another code was sent...";

    Auth.verifyCurrentUserAttribute(ResendCodeComponent.COGNITO_PHONE_NUMBER_ATTRIBUTE);

    setTimeout(() => {
      this.infoMessage = null;
    }, 3000);
  }
}
